import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  MdCheck,
  MdLocalShipping,
  MdReceipt,
  MdArrowForward,
  MdArrowBack,
  MdPrint,
  MdMyLocation,
} from 'react-icons/md';
import Breadcrumb from '../components/shared/Breadcrumb';
import Carousel from '../components/shared/Carousel';
import PopularCategories from '../components/shared/PopularCategories';
import { getOrder, OrderStatus } from '../services/ordersApi';
import { getDeliveryForOrder, DeliveryStop, DeliveryStopStatus } from '../services/deliveriesApi';
import { listMyAddresses } from '../services/addressesApi';
import { getLiveLocations, DriverLocation } from '../services/driverLocationsApi';
import image61 from '../assets/image61.png';
import image62 from '../assets/image62.png';
import image63 from '../assets/image63.png';
import image67 from '../assets/image67.png';
import '../styles/OrderDetailPage.css';

interface OrderItem {
  name: string;
  qty: number;
  price: number;
  image: string;
}

type UiStatus = 'processing' | 'shipped' | 'delivered' | 'cancelled';

interface Order {
  orderNumber: string;
  items: OrderItem[];
  total: number;
  subtotal: number;
  shipping: number;
  tax: number;
  status: UiStatus;
  date: string;
  paymentMethod: string;
  trackingNumber?: string;
  estimatedDelivery: string;
  address: {
    name: string;
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone: string;
    email: string;
  };
}

const STATUS_CONFIG: Record<UiStatus, { label: string; color: string; bg: string }> = {
  processing: { label: 'Processing', color: '#3B6EF6', bg: 'rgba(59,110,246,0.08)' },
  shipped: { label: 'Shipped', color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  delivered: { label: 'Delivered', color: '#10B981', bg: 'rgba(16,185,129,0.08)' },
  cancelled: { label: 'Cancelled', color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
};

/** Backend OrderStatus → the page's coarse UI bucket. */
function statusToUi(s: OrderStatus): UiStatus {
  if (s === 'shipped') return 'shipped';
  if (s === 'delivered' || s === 'invoiced') return 'delivered';
  if (s === 'cancelled') return 'cancelled';
  return 'processing';
}

const fmtDate = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

/** Delivery-stop status presentation (mirrors the web app's customer view). */
const DELIVERY_STATUS: Record<DeliveryStopStatus, { label: string; color: string }> = {
  pending: { label: 'Scheduled', color: '#3B6EF6' },
  in_transit: { label: 'Out for delivery', color: '#F59E0B' },
  delivered: { label: 'Delivered', color: '#10B981' },
  failed: { label: 'Delivery failed', color: '#EF4444' },
  skipped: { label: 'Skipped', color: '#6B7280' },
};

const PROMO_SLIDES = [
  { id: 1, image: image62, tag: 'WHOLESALE DEALS', title: 'Future-Proof Your Inventory', subtitle: 'Lock in wholesale pricing today.', cta: { label: 'Shop Now' }, bgColor: '#1A1040' },
  { id: 2, image: image61, tag: 'LIMITED TIME', title: 'Beverages at Bulk Prices', subtitle: 'Exclusive partner discounts.', cta: { label: 'View Deals' }, bgColor: '#2D1A0E' },
  { id: 3, image: image63, tag: 'ECO-FRIENDLY', title: 'Natural & Organic Range', subtitle: 'Delivered fast to your store.', cta: { label: 'Explore Now' }, bgColor: '#0A2E1A' },
];

const TIMELINE_STEPS = ['Order Placed', 'Processing', 'Shipped', 'Delivered'];

const OrderDetailPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [delivery, setDelivery] = useState<DeliveryStop | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // VP-CUST-003 / VP-FIELD-001 (ecommerce half): live driver location, in
  // addition to the existing polling-based delivery-stop status above.
  // GET /driver-locations/live is scoped server-side for the Customer role
  // to just the driver currently out on one of this customer's own active
  // stops (getLiveLocationsForCustomer) — not a fleet-wide map.
  const [driverLocation, setDriverLocation] = useState<DriverLocation | null>(null);

  // The route param may be a raw id ("123") or the display form ("VIN-123").
  const numericId = orderId ? parseInt(orderId.replace(/\D/g, ''), 10) : NaN;

  useEffect(() => {
    if (!Number.isFinite(numericId)) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        // Order is required; address + delivery are best-effort enrichments.
        const o = await getOrder(numericId);
        const [addresses, deliveryStop] = await Promise.all([
          listMyAddresses().catch(() => []),
          getDeliveryForOrder(numericId).catch(() => null),
        ]);
        if (cancelled) return;

        const items: OrderItem[] = (o.items ?? []).map((i) => ({
          name: i.product_name ?? i.sku_code ?? `SKU #${i.sku_id}`,
          qty: i.quantity,
          price: i.unit_price,
          image: image67, // backend doesn't return product images on order items
        }));
        const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
        const tax = o.tax_total ?? 0;
        const total = o.total_amount ?? subtotal + tax;
        const addr = addresses.find((a) => a.id === o.shipping_address_id);

        setDelivery(deliveryStop);
        setOrder({
          orderNumber: `VIN-${o.id}`,
          items,
          total,
          subtotal,
          shipping: Math.max(0, total - subtotal - tax),
          tax,
          status: statusToUi(o.status),
          date: fmtDate(o.created_at ?? o.order_date),
          paymentMethod: 'Direct Bank Transfer',
          estimatedDelivery:
            fmtDate(deliveryStop?.scheduled_window_end) ||
            fmtDate(o.delivered_at) ||
            '—',
          address: addr
            ? {
                name: o.customer_name ?? 'Customer',
                street: [addr.address_line1, addr.address_line2].filter(Boolean).join(', '),
                city: addr.city,
                state: addr.state,
                zip: addr.zip_code,
                country: 'United States',
                phone: deliveryStop?.customer_phone ?? '',
                email: '',
              }
            : { name: o.customer_name ?? 'Customer', street: '', city: '', state: '', zip: '', country: 'United States', phone: '', email: '' },
        });
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [numericId]);

  // Poll live driver location while this order's delivery is out for
  // delivery. Additive to the existing delivery-stop polling above — this
  // is a separate real-time signal (GPS ping), not a replacement.
  useEffect(() => {
    if (delivery?.status !== 'in_transit') {
      setDriverLocation(null);
      return;
    }
    let cancelled = false;
    const loadLocation = () => {
      getLiveLocations()
        .then((locs) => {
          if (cancelled) return;
          const match = locs.find((l) => l.delivery_route_id === delivery.delivery_route_id) ?? locs[0] ?? null;
          setDriverLocation(match);
        })
        .catch(() => {
          if (!cancelled) setDriverLocation(null);
        });
    };
    loadLocation();
    const interval = setInterval(loadLocation, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [delivery?.status, delivery?.delivery_route_id]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px', color: 'var(--gray-400)' }}>
        <p style={{ fontSize: '1.1rem' }}>Loading order…</p>
      </div>
    );
  }
  if (notFound || !order) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px', color: 'var(--gray-500)' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: 8 }}>Order not found</h2>
        <p style={{ marginBottom: 20 }}>We couldn't find this order on your account.</p>
        <button
          onClick={() => navigate('/orders')}
          style={{ padding: '10px 22px', borderRadius: 8, border: 'none', background: '#2B7FFF', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
        >
          Back to Orders
        </button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[order.status];
  const statusIndex = order.status === 'delivered' ? 3 : order.status === 'shipped' ? 2 : order.status === 'cancelled' ? 1 : 1;
  const deliveryCfg = delivery ? DELIVERY_STATUS[delivery.status] : null;

  return (
    <div className="order-detail">
      <Breadcrumb extra={`Order #${order.orderNumber}`} />

      {/* ── Header ── */}
      <section className="order-detail__header-section">
        <div className="order-detail__header">
          <button className="order-detail__back" onClick={() => navigate('/orders')}>
            <MdArrowBack /> Back to Orders
          </button>
          <div className="order-detail__header-row">
            <div>
              <h1 className="order-detail__title">Order #{order.orderNumber}</h1>
              <span className="order-detail__date">Placed on {order.date}</span>
            </div>
            <div className="order-detail__header-actions">
              <span className="order-detail__status" style={{ color: statusCfg.color, background: statusCfg.bg }}>
                {statusCfg.label}
              </span>
              <button className="order-detail__print" onClick={() => window.print()}>
                <MdPrint /> Print
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Timeline ── */}
      <section className="order-detail__timeline-section">
        <div className="order-detail__card">
          <h2 className="order-detail__card-title">Order Progress</h2>
          <div className="order-detail__timeline">
            <div className="order-detail__timeline-track">
              <div className="order-detail__timeline-fill" style={{ width: `${(statusIndex / 3) * 100}%` }} />
            </div>
            <div className="order-detail__timeline-steps">
              {TIMELINE_STEPS.map((step, i) => (
                <div key={step} className={`order-detail__tl-step ${i <= statusIndex ? 'order-detail__tl-step--done' : ''} ${i === statusIndex ? 'order-detail__tl-step--active' : ''}`}>
                  <div className="order-detail__tl-dot">
                    {i <= statusIndex ? <MdCheck /> : <span>{i + 1}</span>}
                  </div>
                  <span className="order-detail__tl-label">{step}</span>
                  <span className="order-detail__tl-date">
                    {i === 0 ? order.date : i === 3 ? order.estimatedDelivery : i <= statusIndex ? 'Completed' : 'Pending'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {order.trackingNumber && (
            <div className="order-detail__tracking">
              <MdLocalShipping /> Tracking: <strong>{order.trackingNumber}</strong>
            </div>
          )}
        </div>
      </section>

      {/* ── Delivery tracking (live status from /customers/me/deliveries) ── */}
      <section className="order-detail__timeline-section">
        <div className="order-detail__card">
          <h2 className="order-detail__card-title">
            <MdLocalShipping className="order-detail__card-icon" /> Delivery Tracking
          </h2>
          {delivery && deliveryCfg ? (
            <div className="order-detail__info-pairs">
              <div className="order-detail__info-pair">
                <span>Status</span>
                <span style={{ color: deliveryCfg.color, fontWeight: 700 }}>{deliveryCfg.label}</span>
              </div>
              {(delivery.scheduled_window_start || delivery.scheduled_window_end) && (
                <div className="order-detail__info-pair">
                  <span>Scheduled window</span>
                  <span>
                    {delivery.scheduled_window_start
                      ? new Date(delivery.scheduled_window_start).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                      : '—'}
                    {delivery.scheduled_window_end
                      ? ` – ${new Date(delivery.scheduled_window_end).toLocaleString('en-US', { timeStyle: 'short' })}`
                      : ''}
                  </span>
                </div>
              )}
              {delivery.completed_at && (
                <div className="order-detail__info-pair">
                  <span>Delivered at</span>
                  <span className="order-detail__green">
                    {new Date(delivery.completed_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>
              )}
              {delivery.status === 'failed' && delivery.failure_reason && (
                <div className="order-detail__info-pair">
                  <span>Reason</span>
                  <span>{delivery.failure_reason}</span>
                </div>
              )}
              {(delivery.pod_photo_url || delivery.pod_signature_url) && (
                <div className="order-detail__info-pair">
                  <span>Proof of delivery</span>
                  <span>
                    {delivery.pod_photo_url && (
                      <a href={delivery.pod_photo_url} target="_blank" rel="noreferrer" style={{ color: '#2B7FFF', marginRight: 12 }}>Photo</a>
                    )}
                    {delivery.pod_signature_url && (
                      <a href={delivery.pod_signature_url} target="_blank" rel="noreferrer" style={{ color: '#2B7FFF' }}>Signature</a>
                    )}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="order-detail__info-muted" style={{ margin: 0 }}>
              {order.status === 'cancelled'
                ? 'This order was cancelled.'
                : 'Your order is being prepared. Delivery scheduling details will appear here once it’s out for delivery.'}
            </p>
          )}

          {/* Live driver location — additive to the delivery-stop status above.
              Only appears once the stop is in_transit and a driver has a
              recent GPS ping scoped to this customer's own active delivery. */}
          {delivery?.status === 'in_transit' && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--gray-100)' }}>
              {driverLocation ? (
                <div className="order-detail__info-pairs">
                  <div className="order-detail__info-pair">
                    <span><MdMyLocation style={{ verticalAlign: 'middle', marginRight: 4, color: '#10B981' }} />Driver</span>
                    <span style={{ fontWeight: 700 }}>
                      {driverLocation.driver_name || `Driver #${driverLocation.driver_id}`}
                    </span>
                  </div>
                  {driverLocation.route_name && (
                    <div className="order-detail__info-pair">
                      <span>Route</span>
                      <span>{driverLocation.route_name}</span>
                    </div>
                  )}
                  <div className="order-detail__info-pair">
                    <span>Last position</span>
                    <span>
                      {/* NUMERIC columns come back as strings from pg — Number()
                          before toFixed, same defensive pattern the web app
                          uses for total_amount etc. */}
                      {Number(driverLocation.latitude).toFixed(4)}, {Number(driverLocation.longitude).toFixed(4)}
                    </span>
                  </div>
                  {driverLocation.speed_kmh != null && (
                    <div className="order-detail__info-pair">
                      <span>Speed</span>
                      <span>{Number(driverLocation.speed_kmh).toFixed(0)} km/h</span>
                    </div>
                  )}
                  <div className="order-detail__info-pair">
                    <span>Updated</span>
                    <span className="order-detail__green">
                      {new Date(driverLocation.captured_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="order-detail__info-muted" style={{ margin: 0 }}>
                  Your driver is on the way — live position isn't available yet.
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Main: Items + Info ── */}
      <section className="order-detail__main">
        <div className="order-detail__main-grid">
          {/* Left: Items */}
          <div className="order-detail__card order-detail__card--items">
            <h2 className="order-detail__card-title">
              Order Items <span className="order-detail__items-count">{order.items.length} products</span>
            </h2>
            <div className="order-detail__items">
              {order.items.map((item, i) => (
                <div key={i} className="order-detail__item">
                  <img src={item.image} alt={item.name} className="order-detail__item-img" />
                  <div className="order-detail__item-info">
                    <span className="order-detail__item-name">{item.name}</span>
                    <span className="order-detail__item-meta">Qty: {item.qty} × ${item.price.toFixed(2)}</span>
                  </div>
                  <span className="order-detail__item-total">${(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>

            {/* Totals */}
            <div className="order-detail__totals">
              <div className="order-detail__total-row"><span>Subtotal</span><span>${order.subtotal.toFixed(2)}</span></div>
              <div className="order-detail__total-row"><span>Shipping</span><span className={order.shipping === 0 ? 'order-detail__free' : ''}>{order.shipping === 0 ? 'Free' : `$${order.shipping.toFixed(2)}`}</span></div>
              <div className="order-detail__total-row"><span>Tax</span><span>${order.tax.toFixed(2)}</span></div>
              <div className="order-detail__total-row order-detail__total-row--final"><span>Total Paid</span><span>${order.total.toFixed(2)}</span></div>
            </div>
          </div>

          {/* Right: Sidebar */}
          <div className="order-detail__sidebar">
            {/* Shipping */}
            <div className="order-detail__card">
              <h2 className="order-detail__card-title"><MdLocalShipping className="order-detail__card-icon" /> Shipping Address</h2>
              <div className="order-detail__info-block">
                <p className="order-detail__info-name">{order.address.name}</p>
                <p>{order.address.street}</p>
                <p>{order.address.city}, {order.address.state} {order.address.zip}</p>
                <p>{order.address.country}</p>
                <p className="order-detail__info-muted">{order.address.phone}</p>
                <p className="order-detail__info-muted">{order.address.email}</p>
              </div>
            </div>

            {/* Payment */}
            <div className="order-detail__card">
              <h2 className="order-detail__card-title"><MdReceipt className="order-detail__card-icon" /> Payment Info</h2>
              <div className="order-detail__info-pairs">
                <div className="order-detail__info-pair"><span>Method</span><span>{order.paymentMethod}</span></div>
                <div className="order-detail__info-pair"><span>Status</span><span style={{ color: statusCfg.color, fontWeight: 700 }}>{statusCfg.label}</span></div>
                <div className="order-detail__info-pair"><span>Est. Delivery</span><span className="order-detail__green">{order.estimatedDelivery}</span></div>
              </div>
            </div>

            {/* Actions */}
            <div className="order-detail__card-actions">
              <button className="order-detail__btn order-detail__btn--primary" onClick={() => navigate('/products')}>
                Reorder <MdArrowForward />
              </button>
              <Link
                to={`/report-issue?category=delivery&orderId=${numericId}`}
                className="order-detail__btn order-detail__btn--outline"
              >
                Report an Issue
              </Link>
              <Link to="/contact" className="order-detail__btn order-detail__btn--outline">
                Need Help?
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Carousel ── */}
      <section className="order-detail__carousel">
        <Carousel slides={PROMO_SLIDES} autoPlay interval={5000} />
      </section>

      <PopularCategories />
    </div>
  );
};

export default OrderDetailPage;
